import React, { useEffect, useMemo, useRef, useState } from 'react';

// Decode HTML entities (e.g., &lt;p&gt;) so stored, escaped HTML renders correctly
const decodeHtml = (input) => {
  if (typeof input !== 'string' || !input) return '';
  const parser = document.createElement('textarea');
  parser.innerHTML = input;
  return parser.value;
};

// Lightweight contentEditable-based RTE to avoid extra deps
// value: HTML string
// onChange: (html) => void
const RichTextEditor = ({ label = 'Description', value, onChange, placeholder = 'Type here...', height = 220, readOnly = false }) => {
  const [showCode, setShowCode] = useState(false);
  const editorRef = useRef(null);

  const decodedValue = useMemo(() => decodeHtml(value || ''), [value]);

  useEffect(() => {
    if (!showCode && editorRef.current && typeof decodedValue === 'string') {
      // keep editor HTML in sync when external value changes
      if (editorRef.current.innerHTML !== decodedValue) {
        editorRef.current.innerHTML = decodedValue || '';
      }
    }
  }, [decodedValue, showCode]);

  const exec = (cmd, arg = null) => {
    document.execCommand(cmd, false, arg);
    if (editorRef.current) {
      onChange?.(editorRef.current.innerHTML);
    }
  };

  const handleInput = () => {
    onChange?.(editorRef.current?.innerHTML || '');
  };

  const baseAreaStyle = {
    border: '1px solid #ddd',
    padding: 12,
    minHeight: height,
    borderRadius: 6,
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    overflowY: 'auto',
    overflowX: 'hidden',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    whiteSpace: 'pre-wrap',
    lineHeight: '1.6',
    fontSize: '14px',
    backgroundColor: readOnly ? '#f9fafb' : '#fff',
    color: '#1f2937',
  };

  const editorStyle = {
    ...baseAreaStyle,
    outline: 'none',
    cursor: 'text',
  };

  return (
    <div className="form-group full-width" style={{ width: '100%' }}>
      {label && <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, color: '#374151' }}>{label}</label>}
      {!readOnly && (
        <div className="rte-toolbar" style={{ 
          display: 'flex', 
          gap: 6, 
          marginBottom: 8, 
          alignItems: 'center', 
          flexWrap: 'nowrap',
          padding: '8px',
          backgroundColor: '#f3f4f6',
          borderRadius: 6,
          border: '1px solid #e5e7eb',
          overflowX: 'auto'
        }}>
          <button type="button" className="secondary-btn sm" onClick={() => exec('bold')} title="Bold" style={{ fontWeight: 'bold' }}>B</button>
          <button type="button" className="secondary-btn sm" onClick={() => exec('italic')} title="Italic" style={{ fontStyle: 'italic' }}>I</button>
          <button type="button" className="secondary-btn sm" onClick={() => exec('underline')} title="Underline" style={{ textDecoration: 'underline' }}>U</button>
          <button type="button" className="secondary-btn sm" onClick={() => exec('insertUnorderedList')} title="Bullet List">• List</button>
          <button type="button" className="secondary-btn sm" onClick={() => exec('insertOrderedList')} title="Numbered List">1. List</button>
          <button type="button" className="secondary-btn sm" onClick={() => exec('createLink', prompt('Enter URL', 'https://') || '')} title="Insert Link">Link</button>
          <select className="secondary-btn sm" onChange={(e) => exec('fontSize', e.target.value)} defaultValue="3" style={{ padding: '4px 8px', minWidth: 'auto', width: 'auto' }} title="Font Size">
            <option value="1">10px</option>
            <option value="2">12px</option>
            <option value="3">14px</option>
            <option value="4">16px</option>
            <option value="5">18px</option>
            <option value="6">24px</option>
            <option value="7">32px</option>
          </select>
          <input type="color" title="Text color" onChange={(e) => exec('foreColor', e.target.value)} style={{ width: 32, height: 28, cursor: 'pointer', border: '1px solid #d1d5db', borderRadius: 4 }} />
          <button type="button" className="secondary-btn sm" onClick={() => setShowCode(s => !s)} title="Toggle HTML Code">{showCode ? '</>' : '<>'}</button>
        </div>
      )}
      {readOnly ? (
        <div className="text-area" style={baseAreaStyle}
          dangerouslySetInnerHTML={{ __html: decodedValue || '<p style="color: #9ca3af;">No description provided</p>' }} />
      ) : showCode ? (
        <textarea
          className="text-area"
          style={{ ...baseAreaStyle, height, fontFamily: 'monospace' }}
          value={decodedValue || ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder="Enter HTML code..."
        />
      ) : (
        <div
          ref={editorRef}
          className="text-area"
          style={editorStyle}
          contentEditable={true}
          data-placeholder={placeholder}
          onInput={handleInput}
          suppressContentEditableWarning
          onFocus={(e) => {
            e.currentTarget.style.borderColor = '#3b82f6';
            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = '#ddd';
            e.currentTarget.style.boxShadow = 'none';
          }}
        />
      )}
      <style>{`
        .text-area[contenteditable="true"]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }
        .text-area[contenteditable="true"]:focus {
          outline: none;
        }
      `}</style>
    </div>
  );
}
;

export default RichTextEditor;
